import { useState } from 'react';

/**
 * Empty error set for validators whose fields are all optional. Shapes with a
 * required field (a per-row array, say) pass their own constant instead.
 */
export const NO_FIELD_ERRORS = {};

export interface DeferredValidationErrors<TErrors> {
  visibleErrors: TErrors;
  revealErrors: () => void;
  hideErrors: () => void;
}

/**
 * Keeps validation errors hidden until the first failed submit, after which
 * they update live as the user fixes fields.
 *
 * `emptyErrors` is returned as-is while errors are hidden, so it has to be a
 * stable reference - a module-level constant, not an inline literal. It is
 * NoInfer so the error shape is fixed by `errors` alone.
 */
export function useDeferredValidationErrors<TErrors extends object>(
  errors: TErrors,
  emptyErrors: NoInfer<TErrors>
): DeferredValidationErrors<TErrors> {
  const [errorsRevealed, setErrorsRevealed] = useState(false);

  return {
    visibleErrors: errorsRevealed ? errors : emptyErrors,
    revealErrors: () => setErrorsRevealed(true),
    hideErrors: () => setErrorsRevealed(false),
  };
}
